import { useEffect, useState } from "react";
import { api } from "./api";
import { Compliance, type ComplianceTab } from "./components/Compliance";
import { Consent } from "./components/Consent";
import { Documents } from "./components/Documents";
import { DraftEditor } from "./components/DraftEditor";
import { Login } from "./components/Login";
import { Memory, type MemoryTab } from "./components/Memory";
import { Research } from "./components/Research";
import { Shell } from "./components/Shell";
import { TemplateEditor } from "./components/TemplateEditor";
import { match, navigate, usePath } from "./router";
import type { User } from "./types";

type State =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "authenticated"; user: User };

export function App() {
  const [state, setState] = useState<State>({ status: "loading" });

  // The session is an httpOnly cookie, so the only way to know whether one
  // exists is to ask. A 401 here is the normal path, not an error.
  useEffect(() => {
    api
      .me()
      .then((user) => setState({ status: "authenticated", user }))
      .catch(() => setState({ status: "anonymous" }));
  }, []);

  async function logout() {
    try {
      await api.logout();
    } finally {
      setState({ status: "anonymous" });
    }
  }

  if (state.status === "loading") {
    return (
      <div className="centered">
        <span className="muted">se încarcă…</span>
      </div>
    );
  }

  // The URL is kept through sign-in: a link to a draft opened while signed
  // out lands on the draft after logging in, not on the chat.
  if (state.status === "anonymous") {
    return <Login onLogin={(user) => setState({ status: "authenticated", user })} />;
  }

  if (!state.user.consented_at) {
    return (
      <Consent
        user={state.user}
        onAccept={(user) => setState({ status: "authenticated", user })}
      />
    );
  }

  return <Routes user={state.user} onLogout={logout} />;
}

const COMPLIANCE_TABS: Record<string, ComplianceTab> = {
  "": "profile",
  obligations: "obligations",
  register: "register",
};

const MEMORY_TABS: Record<string, MemoryTab> = {
  "": "company",
  people: "people",
  preferences: "preferences",
  files: "files",
};

function Routes({ user, onLogout }: { user: User; onLogout: () => void }) {
  const path = usePath();
  const [, section = "", sub = ""] = path.split("/");

  // Anything unknown goes to the chat, replacing rather than pushing so the
  // back button does not return to a dead address.
  const known =
    (section === "chat" && path.split("/").length <= 3) ||
    (section === "documents" &&
      (path.split("/").length <= 3 || !!match("/documents/models/:slug", path))) ||
    (section === "compliance" && sub in COMPLIANCE_TABS) ||
    (section === "memory" && sub in MEMORY_TABS);
  useEffect(() => {
    if (!known) navigate("/chat", { replace: true });
  }, [known]);
  if (!known) return null;

  if (section === "chat") {
    // One instance across conversations: the list in the sidebar and an
    // unsent draft survive switching threads; the id is just a prop.
    return <Research user={user} onLogout={onLogout} conversationId={sub || null} />;
  }

  if (section === "documents") {
    const draft = match("/documents/:id", path);
    const model = match("/documents/models/:slug", path);
    return (
      <Shell user={user} section="documents" onLogout={onLogout}>
        {model ? (
          <TemplateEditor key={model.slug} slug={model.slug} />
        ) : draft && draft.id !== "models" && draft.id !== "mine" ? (
          <DraftEditor key={draft.id} draftId={draft.id} />
        ) : (
          <Documents
            tab={sub === "models" ? "models" : sub === "mine" ? "mine" : "drafts"}
            canEdit={!!user.is_admin}
          />
        )}
      </Shell>
    );
  }

  if (section === "compliance") {
    return (
      <Shell user={user} section="compliance" onLogout={onLogout}>
        <Compliance tab={COMPLIANCE_TABS[sub]} />
      </Shell>
    );
  }

  return (
    <Shell user={user} section="memory" onLogout={onLogout}>
      <Memory tab={MEMORY_TABS[sub]} />
    </Shell>
  );
}
